import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Loader2, Printer, FileText } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import "@/cap-print.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO = `${process.env.PUBLIC_URL || ""}/cap-logo.png`;
const capApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const fmtDate = (s) => {
  if (!s) return "__________";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
};

const B = ({ children }) => <strong>{children ? children : "__________"}</strong>;

const DRow = ({ label, value }) => (
  <div className="flex gap-2">
    <span className="font-semibold">{label}:</span>
    <span>{value || "__________"}</span>
  </div>
);

function Gate({ onOk }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await capApi.post("/cap/auth", { password: pw });
      localStorage.setItem("cap_token", data.token);
      onOk();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Xəta baş verdi");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-[#0a0a0f]">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rounded-3xl p-8 bg-white/5 border border-white/10"
        data-testid="cap-gate-form"
      >
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#2997FF] to-[#0a84ff] flex items-center justify-center mb-5">
          <FileText className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white">Satış Sənədi</h1>
        <p className="text-slate-400 text-sm mb-6">Girişi qorunan bölmə</p>
        <label className="text-sm text-slate-300 mb-1.5 block">Şifrə</label>
        <Input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="••••••••"
          data-testid="cap-password-input"
          className="bg-white/5 border-white/10 h-11 text-white"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="cap-login-btn"
          className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-black font-medium disabled:opacity-50 hover:bg-slate-200 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Daxil ol
        </button>
      </motion.form>
    </div>
  );
}

export default function CapPortal() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("cap_token"));
  const [f, setF] = useState({
    ad: "", ata: "", soyad: "", fin: "", seriya: "",
    model: "", imei: "", price: "", date: todayStr(), gender: "oglu",
  });

  if (!authed) return <Gate onOk={() => setAuthed(true)} />;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const nameOrder = [f.soyad, f.ad, f.ata].filter(Boolean).join(" ");
  const suffix = f.gender === "qizi" ? "qızı" : "oğlu";
  const fullName = nameOrder + (f.ata ? ` ${suffix}` : "");

  const field = (label, key, placeholder, type = "text") => (
    <div>
      <label className="text-xs text-slate-400 mb-1 block">{label}</label>
      <Input
        type={type}
        value={f[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
        data-testid={`cap-${key}`}
        className="bg-white/5 border-white/10 h-11 text-white"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <header className="no-print sticky top-0 z-30 bg-[#0a0a0f]/90 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#2997FF]" />
          <span className="font-semibold">Satış Sənədi</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            data-testid="cap-print-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2997FF] text-white text-sm font-medium hover:bg-[#0a84ff] transition-colors"
          >
            <Printer className="w-4 h-4" /> Çap et
          </button>
          <button
            onClick={() => {
              localStorage.removeItem("cap_token");
              setAuthed(false);
            }}
            data-testid="cap-logout-btn"
            className="p-2 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:text-white"
            title="Çıxış"
          >
            <Lock className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-2 gap-6">
        <div className="no-print rounded-2xl bg-white/5 border border-white/10 p-4 h-fit">
          <h2 className="font-semibold mb-4">Məlumatları daxil edin</h2>
          <div className="grid grid-cols-2 gap-3">
            {field("Ad", "ad", "Ad")}
            {field("Ata adı", "ata", "Ata adı")}
            {field("Soyad", "soyad", "Soyad")}
            {field("FİN kodu", "fin", "7ABC123")}
            {field("Ş/V seriya və nömrə", "seriya", "AA 1234567")}
            {field("Telefon modeli", "model", "iPhone 15 Pro Max")}
            {field("IMEI", "imei", "356789...")}
            {field("Satış məbləği (AZN)", "price", "1200", "number")}
          </div>
          <div className="mt-3">
            <label className="text-xs text-slate-400 mb-1 block">Cins (ata adı sonluğu)</label>
            <div className="grid grid-cols-2 gap-2" data-testid="cap-gender-toggle">
              {[["oglu", "oğlu"], ["qizi", "qızı"]].map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => set("gender", val)}
                  data-testid={`cap-gender-${val}`}
                  className={`h-11 rounded-lg border text-sm font-medium transition-colors ${
                    f.gender === val
                      ? "bg-[#2997FF] border-[#2997FF] text-white"
                      : "bg-white/5 border-white/10 text-slate-300 hover:text-white"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3">
            {field("Satış tarixi", "date", "", "date")}
          </div>
        </div>

        <div className="cap-preview overflow-x-auto">
          <div id="a4-sheet" className="a4-sheet bg-white text-black mx-auto shadow-2xl" data-testid="cap-a4-sheet">
            <div className="flex items-center justify-between border-b border-gray-300 pb-3 mb-6">
              <img src={LOGO} alt="iStore.az" className="h-11 w-auto object-contain" crossOrigin="anonymous" />
            </div>
            <h1 className="text-center font-bold text-[17px] mb-6 uppercase">
              Mobil telefonun satışı haqqında təsdiq
            </h1>
            <p className="mb-3 leading-relaxed text-justify">
              Mən, <B>{fullName}</B>, şəxsiyyət vəsiqəsi məlumatları əsasında, bu sənəddə göstərilən
              mobil telefonu öz iradəmlə satdığımı təsdiq edirəm.
            </p>
            <p className="mb-3 leading-relaxed text-justify">
              Mənə məxsus olan <B>{f.model}</B>, IMEI nömrəsi <B>{f.imei}</B> olan mobil telefonu{" "}
              <B>{fmtDate(f.date)}</B> tarixində <B>{f.price}</B> AZN məbləğində satmışam.
            </p>
            <p className="mb-1 font-semibold">Təsdiq edirəm ki:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1 leading-relaxed">
              <li>yuxarıda qeyd olunan mobil telefon mənə məxsusdur;</li>
              <li>
                mobil telefon oğurluq yolu ilə əldə edilməyib və mənim bildiyimə görə onun üzərində
                üçüncü şəxslərin mülkiyyət və ya digər hüquq iddiaları yoxdur;
              </li>
              <li>mobil telefonun satışına hüquqi maneə yoxdur;</li>
              <li>
                telefonu öz iradəmlə satıram və satışla bağlı göstərilən məlumatların düzgün olduğunu
                təsdiq edirəm;
              </li>
              <li>satış məbləğini <B>{f.price}</B> AZN olaraq qəbul etdiyimi təsdiq edirəm;</li>
              <li>
                telefonun IMEI nömrəsinin və digər məlumatlarının bu sənəddə düzgün göstərildiyini
                təsdiq edirəm.
              </li>
            </ul>
            <p className="mb-6 leading-relaxed text-justify">
              Bu sənədi oxudum, məzmunu mənə aydındır və göstərilən məlumatların düzgün olduğunu təsdiq
              edərək imzalayıram.
            </p>

            <div className="space-y-1.5 mb-8 text-[13px]">
              <DRow label="Satıcının adı, ata adı, soyadı" value={fullName} />
              <DRow label="Şəxsiyyət vəsiqəsinin FİN kodu" value={f.fin} />
              <DRow label="Şəxsiyyət vəsiqəsinin seriyası və nömrəsi" value={f.seriya} />
              <DRow label="Telefon modeli" value={f.model} />
              <DRow label="IMEI" value={f.imei} />
              <DRow label="Satış məbləği" value={f.price ? `${f.price} AZN` : ""} />
              <DRow label="Satış tarixi" value={fmtDate(f.date)} />
            </div>

            <div className="flex justify-between items-end mt-12 mb-2 text-[13px]">
              <div>Satıcının imzası: <span className="inline-block w-40 border-b border-gray-500 align-bottom">&nbsp;</span></div>
              <div>Tarix: <span className="inline-block w-28 border-b border-gray-500 align-bottom">&nbsp;</span></div>
            </div>

            <div className="footer text-center text-[11px] text-gray-500 mt-10 pt-3 border-t border-gray-300">
              www.istore.az · 077 600 97 97
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
